/**
 * Firebase Client SDK — initialization and exports
 *
 * All Firebase services are initialized here once and exported for use
 * across the app. Environment variables are injected by Vite at build time.
 *
 * Services initialized:
 *   - Firestore (database)
 *   - Auth (Google sign-in for admin)
 *   - Storage (image uploads for About, Gallery)
 *   - Analytics (replaces standalone GA4 gtag.js)
 *   - Performance Monitoring (lazy-loaded after 5s to avoid intercepting initial Firestore loads)
 *
 * Required env vars (in .env):
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_STORAGE_BUCKET
 *   VITE_FIREBASE_MESSAGING_SENDER_ID
 *   VITE_FIREBASE_APP_ID
 *   VITE_FIREBASE_MEASUREMENT_ID (optional — enables Firebase Analytics)
 */
import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');
export const googleProvider = new GoogleAuthProvider();

// Firebase Analytics — replaces standalone GA4 gtag.js script
// isSupported() returns false in SSR, test environments, and when cookies are blocked.
// Initialized lazily (no top-level await) for build target compatibility.
export let analytics = null;
isSupported().then((yes) => {
  if (yes) analytics = getAnalytics(app);
});

// Firebase Performance Monitoring — lazy-loaded after 5 seconds
// Delayed initialization prevents the Performance SDK from intercepting
// Firestore network requests during the critical initial page load.
// The SDK auto-monitors page load timing and HTTP requests once active.
setTimeout(() => {
  import('firebase/performance').then(({ getPerformance }) => {
    try {
      getPerformance(app);
    } catch {
      // Silently fail — perf monitoring is non-critical
    }
  });
}, 5000);

export default app;
